import type { Block, Blocks } from '@w/content-blocks/src/block'
import { sliceChars } from './chars'
import type { CorePoint } from './state'

export type Element = Block & { children: Blocks }
export interface Row {
  block: Element
  siblings: Blocks
  parents: Element[]
  /** The block list `rows` walked from; block-level gaps resolve against it. */
  root: Blocks
}

const ROW_TYPES = new Set<Block['type']>(['p', 'h', 'pre', 'code', 'td'])

export function isRow(block: Block): boolean {
  return ROW_TYPES.has(block.type) && !isFormulaBlock(block)
}

/** A block formula renders its LaTeX source as math (Lexical: a decorator); only its source editor edits the text. */
function isFormulaBlock(block: Block): boolean {
  return block.type === 'pre' && block.latex === true
}

/** A position between two blocks of one block list (`parent` undefined: the document). */
export interface Gap {
  siblings: Blocks
  parent?: Block
  index: number
}

/**
 * The block-level gap a point names. `{host.id, 0}` is always the start of a block host's interior
 * (a non-row block holding blocks, except table structure), whatever it holds; `{host.id, 1}` is after it.
 * Any other non-row block names its edges `{id, 0 | 1}`. `{root, 0}` is the document start.
 * Leaf, atom, `br` and row points name no gap.
 */
export function gapOf(blocks: Blocks, point: CorePoint): Gap | undefined {
  if (point.blockId === 'root') {
    // INV-1: views convert child indexes at their boundary (canonicalPoint, Lexical gapPoint).
    if (point.offset !== 0) throw new Error(`INV-1: child-index point root:${point.offset} reached core`)
    return { siblings: blocks, index: 0 }
  }
  // A committed view's rows know every id they hold, and no id inside a row names a gap.
  if (inCommittedRow(blocks, point.blockId)) return undefined
  const visit = (items: Blocks, parent?: Block): Gap | undefined => {
    for (let index = 0; index < items.length; index++) {
      const block = items[index]
      if (isRow(block)) continue
      if (block.id === point.blockId && point.offset === 0 && isHost(block)) {
        return { siblings: children(block), parent: block, index: 0 }
      }
      if (block.id === point.blockId) return { siblings: items, parent, index: index + (point.offset > 0 ? 1 : 0) }
      const found = visit(children(block), block)
      if (found) return found
    }
    return undefined
  }
  return visit(blocks)
}

/**
 * Identity point of a gap: the next non-row, non-host neighbour (`0`), else the previous non-row
 * neighbour (`1`); between rows the adjacent row's edge; else the start of the parent's interior
 * (`{parent ?? root, 0}`), since `{host.id, 0}` of a next host names its inside.
 */
export function gapPoint(gap: Gap): CorePoint {
  const next = gap.siblings[gap.index] as Block | undefined
  const previous = gap.siblings[gap.index - 1] as Block | undefined
  if (next && !isRow(next) && !isHost(next)) return { blockId: next.id, offset: 0 }
  // The page header is no caret anchor: it is edited in its own fields, so the gap after it is the next row's start.
  if (previous && !isRow(previous) && !isHeader(previous)) return { blockId: previous.id, offset: 1 }
  const nextRow = next && isRow(next) ? rows([next])[0] : undefined
  if (nextRow) return pointAt(nextRow, 0)
  const previousRow = previous && rows([previous])[0]
  if (previousRow) return pointAt(previousRow, plain(previousRow.block).length)
  return { blockId: gap.parent?.id ?? 'root', offset: 0 }
}

/**
 * Identity form of a point: a view child index (`{root, n}`) or any gap point becomes its canonical gap
 * point and an element point inside a row the point of its row offset; text leaf points pass through.
 */
export function canonicalPoint(blocks: Blocks, point: CorePoint): CorePoint {
  if (point.blockId === 'root') return gapPoint({ siblings: blocks, index: Math.min(Math.max(point.offset, 0), blocks.length) })
  const gap = gapOf(blocks, point)
  if (gap) return gapPoint(gap)
  const find = (items: Blocks): Block | undefined => {
    for (const block of items) {
      if (block.id === point.blockId) return block
      const found = find(children(block))
      if (found) return found
    }
    return undefined
  }
  const entries = rows(blocks)
  const indexed = Object.isFrozen(entries) ? rowOf(entries, point.blockId) : undefined
  const row = indexed === undefined ? entries.find((entry) => entry.block.id === point.blockId || find(entry.block.children))
    : entries[indexed]
  const block = row && (row.block.id === point.blockId ? row.block : find(row.block.children))
  if (!row || !block || block.type === 'chars' || block.type === 'text') return point
  return pointAt(row, locate([row], point).offset)
}

/** The first valid point of a document: its first row's start, else the gap before its first block. */
export function firstPoint(blocks: Blocks): CorePoint {
  const first = rows(blocks)[0]
  return first ? pointAt(first, 0) : gapPoint({ siblings: blocks, index: 0 })
}

/** Number of rows of `root` (document order) before a gap. */
export function gapRowIndex(root: Blocks, gap: Gap): number {
  let passed = false
  let count = 0
  const visit = (list: Blocks) => {
    for (const [index, block] of list.entries()) {
      if (passed) return
      if (list === gap.siblings && index === gap.index) passed = true
      if (passed) return
      if (isRow(block)) count += 1
      else visit(children(block))
    }
    if (list === gap.siblings && gap.index >= list.length) passed = true
  }
  visit(root)
  return count
}

/** Row position of a gap: the start of the first row after it in document order, else the last row's end. */
function gapLocation(items: Row[], root: Blocks, gap: Gap): { row: number; offset: number } {
  const next = gapRowIndex(root, gap)
  if (next < items.length) return { row: next, offset: 0 }
  const row = Math.max(0, items.length - 1)
  return { row, offset: items[row] ? plain(items[row].block).length : 0 }
}

/** A block host: a non-row block holding blocks (quote, list item, alert, column…). Grid structure is not one (a table
 * and its rows, a column set: only their slots hold blocks), and a media or page atom's optional children are its
 * payload (an image's label), never hosted blocks. */
export function isHost(block: Block): boolean {
  return !isRow(block) && !isFormulaBlock(block) && !['table', 'tr', 'cols'].includes(block.type) && !ATOM_TYPES.has(block.type) &&
    'children' in block && Array.isArray(block.children)
}

const HEADER_TYPES = new Set<Block['type']>(['pageTitle', 'pageMeta'])

/** The page header (title, metadata): the page's identity, edited in its own fields and never deleted. */
export function isHeader(block: Block): boolean {
  return HEADER_TYPES.has(block.type)
}

export function children(block: Block): Blocks {
  return 'children' in block && Array.isArray(block.children) ? block.children : []
}

/** A `p` or `h` holding only text (no inline chips, links or nested blocks). */
export function isTextRow(block: Block): boolean {
  return (block.type === 'p' || block.type === 'h') &&
    block.children.every(child => child.type === 'chars' || child.type === 'text' || child.type === 'br')
}

const listed = new WeakMap<Blocks, ReadonlySet<Block>>()
/** Whether `block` is an item of `items` itself (not a descendant): O(1) per lookup, one Set per view array. */
export function isListed(items: Blocks, block: Block): boolean {
  let set = listed.get(items)
  if (!set) listed.set(items, set = new Set(items))
  return set.has(block)
}

/** Offset text of one childless inline atom: one caret stop, one grapheme, one word unit. */
export const ATOM = '￼'

/** Childless inline blocks the reader renders visibly (block-viewer.tsx); `criticComment` renders nothing. */
const ATOM_TYPES = new Set<Block['type']>(['mention', 'pages', 'pageLink', 'page', 'img', 'video', 'audio', 'file'])

/** Childless inline atoms carry their content in props (mention uid, page ids, media src), not text. */
export function isAtom(block: Block): boolean {
  return ATOM_TYPES.has(block.type) && !('children' in block && Array.isArray(block.children))
}

/** Row offset text: `br` is `\n` and each atom is one U+FFFC unit. */
export function plain(block: Block): string {
  if (block.type === 'text') return block.text
  if (block.type === 'chars') return block.text.map((part) => part.text).join('')
  if (block.type === 'br') return '\n'
  if (isAtom(block)) return ATOM
  return children(block).map((child) => plain(child)).join('')
}

/** Authored text without atom units, for writes into text-only containers and display. */
export function textOf(block: Block): string {
  return plain(block).replaceAll(ATOM, '')
}

const committedRows = new WeakMap<Blocks, Row[]>()
/**
 * The text rows of `items` in document order. A frozen list is a committed view (crdt views are deep-frozen and never
 * change), so its rows are derived once per list and returned frozen; the editor reads them a dozen times per keystroke
 * (selection mapping, toolbars, menus). The same per-frozen-view memo as `indexViewBlocks` (doc-blocks.ts).
 */
export function rows(items: Blocks, parents: Element[] = [], root: Blocks = items): Row[] {
  if (parents.length > 0 || root !== items || !Object.isFrozen(items)) return collectRows(items, parents, root)
  let cached = committedRows.get(items)
  if (!cached) committedRows.set(items, cached = Object.freeze(collectRows(items, [], items).map(row => Object.freeze(row))) as Row[])
  return cached
}

function collectRows(items: Blocks, parents: Element[], root: Blocks): Row[] {
  return items.flatMap((block) => {
    if (isRow(block)) return [{ block: block as Element, siblings: items, parents, root }]
    return collectRows(children(block), [...parents, block as Element], root)
  })
}

/** Whether `id` names a block inside a row of the committed view `blocks` (false for a draft or a non-row id). */
export function inCommittedRow(blocks: Blocks, id: string): boolean {
  return Object.isFrozen(blocks) && rowOf(rows(blocks), id) !== undefined
}

const frozenIds = new WeakMap<Block, ReadonlySet<string>>()
/**
 * Whether `block` or a descendant has id `id`. A frozen block (a committed view's, shared by every later view and draft
 * that leaves it unchanged) answers from its id set, built once, so a draft's row scan costs a lookup per unchanged row
 * instead of a walk (Lexical keeps unchanged nodes and their keys across updates).
 */
function holdsId(block: Block, id: string): boolean {
  if (!Object.isFrozen(block)) return block.id === id || children(block).some(child => holdsId(child, id))
  let ids = frozenIds.get(block)
  if (!ids) {
    const set = new Set<string>()
    const add = (item: Block) => { set.add(item.id); for (const child of children(item)) add(child) }
    add(block)
    frozenIds.set(block, ids = set)
  }
  return ids.has(id)
}

let lastId = ''
let lastRow = -1
/**
 * Row of `id` in a committed view's rows. The editor looks up the caret's ids many times per keystroke, in a new view
 * each time, and the caret's row keeps its index while typing: the last answer is tried first and confirmed by the row
 * block's id set (`holdsId`), so a keystroke builds no page-sized row index (Lexical and ProseMirror map a selection
 * through the touched node, not a document-wide table). Other ids, or a moved row, fall back to the index. An id held by
 * two rows (a merge artifact; `rowIndex` keeps the first) can keep answering the later row it was first found in.
 */
function rowOf(items: readonly Row[], id: string): number | undefined {
  if (lastId === id) {
    const hint = items[lastRow]
    if (hint && holdsId(hint.block, id)) return lastRow
  }
  const row = rowIndex(items).get(id)
  if (row !== undefined) { lastId = id; lastRow = row }
  return row
}

const rowIndexes = new WeakMap<readonly Row[], Map<string, number>>()
/** Row of every block id inside a committed view's rows (the row block and its inline descendants). */
function rowIndex(items: readonly Row[]): Map<string, number> {
  let index = rowIndexes.get(items)
  if (index) return index
  index = new Map()
  const add = (block: Block, row: number) => {
    if (!index.has(block.id)) index.set(block.id, row)
    for (const child of children(block)) add(child, row)
  }
  for (const [row, entry] of items.entries()) add(entry.block, row)
  rowIndexes.set(items, index)
  return index
}

/**
 * Block `id` inside a row of a frozen top-level list (a view's `content.children`) with its Doc path, from the list's
 * cached rows and row index: rows and their inline descendants are reached through `children` only (`collectRows`),
 * so the path is the one `indexViewBlocks` gives (doc-blocks.ts build) without building that page-sized index.
 * Undefined for an id outside rows (a field's content, a gap).
 */
export function rowBlock(items: Blocks, id: string): { block: Block; path: string } | undefined {
  if (!Object.isFrozen(items)) return undefined
  const entries = rows(items)
  const entry = entries[rowOf(entries, id) ?? -1]
  if (!entry) return undefined
  const find = (block: Block, path: string): { block: Block; path: string } | undefined => {
    if (block.id === id) return { block, path }
    for (const child of children(block)) {
      const found = find(child, `${path}.children.${child.id}`)
      if (found) return found
    }
    return undefined
  }
  return find(entry.block, `content.children.${[...entry.parents, entry.block].map(block => block.id).join('.children.')}`)
}

export function locate(items: Row[], point: CorePoint): { row: number; offset: number } {
  // Without rows every gap is the only position.
  if (items.length === 0) return { row: 0, offset: 0 }
  const root = items[0].root
  // A committed view's rows find the point's row by id; a command's draft rows are scanned in order. No id inside a row
  // names a gap (gapOf skips rows), so a point some row holds needs no gap search.
  const indexed = Object.isFrozen(items) ? rowOf(items, point.blockId)
    : indexOrUndefined(items.findIndex(entry => holdsId(entry.block, point.blockId)))
  const gap = indexed === undefined ? gapOf(root, point) : undefined
  if (gap) return gapLocation(items, root, gap)
  if (indexed !== undefined) {
    const found = rowOffset(items[indexed], point)
    if (found !== -1) return { row: indexed, offset: found }
  }
  throw new Error(`Unknown core point ${point.blockId}`)
}

function indexOrUndefined(index: number): number | undefined {
  return index === -1 ? undefined : index
}

/** Offset of `point` in `entry`'s row text, -1 when the row does not hold it. */
function rowOffset(entry: Row, point: CorePoint): number {
  if (entry.block.id === point.blockId) return entry.block.children.slice(0, point.offset)
    .reduce((sum, block) => sum + plain(block).length, 0)
  let offset = 0
  let found = -1
  const walk = (block: Block) => {
    if (block.id === point.blockId) found = offset + (
      children(block).length > 0
        ? children(block).slice(0, point.offset)
          .reduce((sum, child) => sum + plain(child).length, 0)
        : point.offset
    )
    if (block.type === 'text' || block.type === 'chars' || block.type === 'br' || isAtom(block)) {
      offset += plain(block).length
    } else children(block).forEach((block) => { walk(block) })
  }
  children(entry.block).forEach((block) => { walk(block) })
  return found
}

/**
 * Canonical point of a row offset. Text leaves own their boundaries; an atom or `br` edge that no
 * text leaf can express is anchored to that block (`offset: 0 | 1`), between two of them to the left one.
 */
export function pointAt(row: Row, offset: number): CorePoint {
  let remaining = offset
  let last: CorePoint = { blockId: row.block.id, offset: 0 }
  // The edge after the atom or `br` just crossed, while nothing else has been consumed since.
  let afterUnit: CorePoint | undefined
  const visit = (items: Blocks): CorePoint | undefined => {
    for (const block of items) {
      if (block.type === 'text' || block.type === 'chars') {
        const length = plain(block).length
        last = { blockId: block.id, offset: length }
        if (remaining <= length) return { blockId: block.id, offset: remaining }
        remaining -= length
        afterUnit = undefined
      } else if (block.type === 'br' || isAtom(block)) {
        if (remaining < 1) return afterUnit ?? { blockId: block.id, offset: 0 }
        remaining -= 1
        last = afterUnit = { blockId: block.id, offset: 1 }
      } else {
        const found = visit(children(block))
        if (found) return found
      }
    }
    return undefined
  }
  return visit(row.block.children) ?? last
}

export function leaf(text: string, format = 0): Block {
  return { id: '', type: 'chars', text: [{ id: '', text }], ...(format ? { format } : {}) }
}

/** Slice inline trees, retaining ids on the left and minting only split copies: with `fresh`, a block that starts
 * before `start` (its head went to an earlier slice) gets a new id; a block that starts inside the slice keeps its id,
 * so a peer's concurrent edit inside it survives the rebuild (crdt facade spec "Join keeps identity"). */
export function sliceInline(items: Blocks, start: number, end: number, fresh = false, root?: unknown): Blocks {
  let offset = 0
  return items.flatMap((block): Blocks => {
    const length = plain(block).length
    const from = Math.max(0, start - offset)
    const to = Math.min(length, end - offset)
    offset += length
    if (from >= to) return []
    const copy = { ...block, ...(fresh && from > 0 ? { id: '' } : {}) }
    if (copy.type === 'chars') {
      copy.text = sliceChars(copy.text, from, to, root ?? { content: { children: items } })
    } else if (copy.type === 'text') copy.text = copy.text.slice(from, to)
    else if ('children' in copy) {
      Object.assign(copy, { children: sliceInline(children(block), from, to, fresh, root) })
    }
    return [copy]
  })
}

export function lastOf<T>(items: T[]): T {
  const last = items.at(-1)
  if (!last) throw new Error('Expected a nonempty core path')
  return last
}

/** The block list holding block `id` at any depth, as a gap at its index (`parent` undefined: the document). */
export function blockGap(items: Blocks, id: string, parent?: Block): Gap | undefined {
  const index = items.findIndex((block) => block.id === id)
  if (index >= 0) return { siblings: items, parent, index }
  for (const block of items) {
    const found = blockGap(children(block), id, block)
    if (found) return found
  }
  return undefined
}

/** One read-only traversal for ordinary editing children; in a committed view a row's id or inline id is looked up. */
export function findBlock(items: readonly Block[], id: string): Block | undefined {
  if (Object.isFrozen(items)) {
    const entries = rows(items as Blocks)
    const row = rowOf(entries, id)
    if (row !== undefined) return walkFind([entries[row].block], id)
  }
  return walkFind(items, id)
}

function walkFind(items: readonly Block[], id: string): Block | undefined {
  for (const block of items) {
    if (block.id === id) return block
    const found = walkFind(children(block), id)
    if (found) return found
  }
  return undefined
}
